import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Link
} from '@mui/material';
import {
  AccountBalanceWallet,
  Business,
  CheckCircle,
  Warning,
  Api
} from '@mui/icons-material';

const DigitalWalletSetupDialog = ({ open, onClose, onSave, currentConfig, paymentMethod }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [config, setConfig] = useState({
  walletType: paymentMethod?.id || 'wechat',
    enabled: true,
    testMode: true,
    wechat: {
      merchantId: '',
      appId: '',
      apiKey: '',
      secretKey: '',
      connected: false
    },
    alipay: {
      partnerId: '',
      sellerId: '',
      privateKey: '',
      publicKey: '',
      connected: false
    },
    touchngo: {
      merchantCode: '',
      terminalId: '',
      secretKey: '',
      connected: false
    },
    grabpay: {
      partnerId: '',
      partnerSecret: '',
      merchantId: '',
      connected: false
    },
    shopeepay: {
      partnerId: '',
      merchantId: '',
      apiKey: '',
      secretKey: '',
      connected: false
    },
    // ✅ UPDATED: Real ZaloPay configuration
    zalopay: {
      appId: '',
      key1: '', // MAC Key
      key2: '', // Callback Key
      merchantCode: '',
      businessLicense: '',
      taxCode: '',
      integrationType: 'standard',
      callbackUrl: '',
      connected: false
    },
    // ✅ UPDATED: Real PayLah! configuration
    paylah: {
      merchantAccount: '', // DBS business account
      merchantId: '',
      qrCodeId: '',
      businessUEN: '',
      businessName: '',
      qrType: 'static',
      apiEndpoint: '',
      apiKey: '',
      connected: false
    },
    bigpay: {
      merchantId: '',
      apiKey: '',
      secretKey: '',
      connected: false
    },
    integration: {
      webhookUrl: '',
      callbackUrl: '',
      environment: 'sandbox'
    },
    ...currentConfig
  });

  const walletProviders = {
    wechat: {
      name: 'WeChat Pay',
      description: 'China\'s leading mobile payment platform',
      regions: ['China', 'Hong Kong', 'Malaysia'],
      documentation: 'https://pay.weixin.qq.com/wiki/doc/api/index.html',
      requirements: [
        'WeChat Pay merchant account',
        'Business license verification',
        'API credentials from WeChat'
      ]
    },
    alipay: {
      name: 'Alipay',
      description: 'Global digital payment platform by Ant Group',
      regions: ['China', 'Hong Kong', 'Malaysia', 'Singapore'],
      documentation: 'https://global.alipay.com/docs',
      requirements: [
        'Alipay merchant account',
        'KYC verification',
        'API integration setup'
      ]
    },
    touchngo: {
      name: 'Touch \'n Go eWallet',
      description: 'Malaysia\'s leading digital wallet',
      regions: ['Malaysia'],
      documentation: 'https://developer.tngdigital.com.my',
      requirements: [
        'TNG eWallet merchant account',
        'Business registration in Malaysia',
        'API credentials'
      ]
    },
    grabpay: {
      name: 'GrabPay',
      description: 'Southeast Asia\'s super app payment solution',
      regions: ['Singapore', 'Malaysia', 'Thailand', 'Philippines', 'Vietnam'],
      documentation: 'https://developer.grab.com/docs',
      requirements: [
        'Grab merchant account',
        'Business verification',
        'Partner ID and secret'
      ]
    },
    shopeepay: {
      name: 'ShopeePay',
      description: 'Growing digital wallet across Southeast Asia',
      regions: ['Singapore', 'Malaysia', 'Thailand', 'Philippines', 'Vietnam'],
      documentation: 'https://shopee.com/business',
      requirements: [
        'Shopee merchant account',
        'Business verification',
        'API integration approval'
      ]
    },
    zalopay: {
      name: 'ZaloPay',
      description: 'Vietnam\'s leading payment app by VNG Corporation',
      regions: ['Vietnam'],
      documentation: 'https://docs.zalopay.vn/',
      requirements: [
        'Vietnamese business registration',
        'GPKD (Business License) verification',
        'Tax code (MST) registration',
        'VNG merchant approval process',
        'KYC verification for business owner'
      ],
      setupSteps: [
        'Submit business documents to VNG',
        'Complete KYC verification',
        'Receive App ID and API keys',
        'Configure callback URLs',
        'Test in sandbox environment'
      ]
    },
    // ✅ PayLah specific provider info
    paylah: {
      name: 'PayLah! by DBS',
      description: 'Singapore\'s popular digital wallet by DBS Bank',
      regions: ['Singapore'],
      documentation: 'https://www.dbs.com.sg/business/deposits/paylah-business',
      requirements: [
        'DBS/POSB business bank account',
        'Singapore business registration',
        'UEN (Unique Entity Number)',
        'DBS business banking relationship',
        'PayLah! merchant registration'
      ],
      setupSteps: [
        'Open DBS business account',
        'Apply for PayLah! merchant service',
        'Generate QR code via DBS portal',
        'Configure payment settlement',
        'Test QR code payments'
      ]
    },
    bigpay: {
      name: 'BigPay',
      description: 'AirAsia\'s financial service platform',
      regions: ['Malaysia', 'Singapore'],
      documentation: 'https://www.bigpay.my/business',
      requirements: [
        'BigPay merchant account',
        'Business verification',
        'API credentials from BigPay'
      ]
    }
    
    // ... other providers
  };

  useEffect(() => {
    if (paymentMethod?.id) {
      // ✅ FIXED: Set walletType based on paymentMethod.id
      setConfig(prevConfig => ({
        ...prevConfig,
        walletType: paymentMethod.id, // This is the key fix!
        ...currentConfig
      }));
    } else if (currentConfig && Object.keys(currentConfig).length > 0) {
      setConfig(prevConfig => ({ ...prevConfig, ...currentConfig }));
    }
  }, [currentConfig, paymentMethod]);

  const handleConfigChange = (section, field, value) => {
    setConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleTestConnection = async (walletType) => {
    // Simulate API connection test
    setTimeout(() => {
      setConfig(prev => ({
        ...prev,
        [walletType]: {
          ...prev[walletType],
          connected: true
        }
      }));
    }, 2000);
  };

  const renderWalletConfig = () => {
    const wallet = config.walletType;
    const walletConfig = config[wallet];
    
    // ✅ ADD: Debug logging
    console.log('🔍 DigitalWalletSetupDialog rendering:', {
      walletType: wallet,
      paymentMethodId: paymentMethod?.id,
      paymentMethodName: paymentMethod?.name,
      walletConfig: walletConfig
    });
    
    switch (wallet) {
      case 'wechat':
        return (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <TextField
              fullWidth
              label="Merchant ID"
              value={walletConfig.merchantId}
              onChange={(e) => handleConfigChange(wallet, 'merchantId', e.target.value)}
              placeholder="Your WeChat Pay merchant ID"
            />
            
            <TextField
              fullWidth
              label="App ID"
              value={walletConfig.appId}
              onChange={(e) => handleConfigChange(wallet, 'appId', e.target.value)}
              placeholder="WeChat Mini Program App ID"
            />
            
            <TextField
              fullWidth
              label="API Key"
              type="password"
              value={walletConfig.apiKey}
              onChange={(e) => handleConfigChange(wallet, 'apiKey', e.target.value)}
              placeholder="WeChat Pay API Key"
            />
            
            <TextField
              fullWidth
              label="Secret Key"
              type="password"
              value={walletConfig.secretKey}
              onChange={(e) => handleConfigChange(wallet, 'secretKey', e.target.value)}
              placeholder="API Secret Key"
            />
            
          </Box>
        );
        
      case 'alipay':
        return (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <TextField
              fullWidth
              label="Partner ID"
              value={walletConfig.partnerId}
              onChange={(e) => handleConfigChange(wallet, 'partnerId', e.target.value)}
              placeholder="Alipay Partner ID"
            />
            
            <TextField
              fullWidth
              label="Seller ID"
              value={walletConfig.sellerId}
              onChange={(e) => handleConfigChange(wallet, 'sellerId', e.target.value)}
              placeholder="Alipay Seller ID"
            />
            
            <TextField
              fullWidth
              label="RSA Private Key"
              multiline
              rows={4}
              value={walletConfig.privateKey}
              onChange={(e) => handleConfigChange(wallet, 'privateKey', e.target.value)}
              placeholder="Your RSA private key"
            />
            
            <TextField
              fullWidth
              label="Alipay Public Key"
              multiline
              rows={4}
              value={walletConfig.publicKey}
              onChange={(e) => handleConfigChange(wallet, 'publicKey', e.target.value)}
              placeholder="Alipay's RSA public key"
            />
          </Box>
        );
        
      case 'touchngo':
        return (
          <Box sx={{ display: 'grid', gap: 2 }}>
            <TextField
              fullWidth
              label="Merchant Code"
              value={walletConfig.merchantCode}
              onChange={(e) => handleConfigChange(wallet, 'merchantCode', e.target.value)}
              placeholder="TNG eWallet Merchant Code"
            />
            
            <TextField
              fullWidth
              label="Terminal ID"
              value={walletConfig.terminalId}
              onChange={(e) => handleConfigChange(wallet, 'terminalId', e.target.value)}
              placeholder="Terminal/Device ID"
            />
            
            <TextField
              fullWidth
              label="Secret Key"
              type="password"
              value={walletConfig.secretKey}
              onChange={(e) => handleConfigChange(wallet, 'secretKey', e.target.value)}
              placeholder="API Secret Key"
            />
          </Box>
        );

        case 'grabpay':
          return (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <TextField
                fullWidth
                label="Partner ID"
                value={walletConfig.partnerId}
                onChange={(e) => handleConfigChange('grabpay', 'partnerId', e.target.value)}
                placeholder="Grab Partner ID"
                helperText="Partner ID assigned by Grab"
              />
              
              <TextField
                fullWidth
                label="Partner Secret"
                type="password"
                value={walletConfig.partnerSecret}
                onChange={(e) => handleConfigChange('grabpay', 'partnerSecret', e.target.value)}
                placeholder="Grab Partner Secret"
                helperText="Secret key for API authentication"
              />
              
              <TextField
                fullWidth
                label="Merchant ID"
                value={walletConfig.merchantId}
                onChange={(e) => handleConfigChange('grabpay', 'merchantId', e.target.value)}
                placeholder="Grab Merchant ID"
                helperText="Your merchant identifier with Grab"
              />
            </Box>
          );

        case 'shopeepay':
          return (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <TextField
                fullWidth
                label="Partner ID"
                value={walletConfig.partnerId}
                onChange={(e) => handleConfigChange('shopeepay', 'partnerId', e.target.value)}
                placeholder="Shopee Partner ID"
                helperText="Partner ID from Shopee business portal"
              />
              
              <TextField
                fullWidth
                label="Merchant ID"
                value={walletConfig.merchantId}
                onChange={(e) => handleConfigChange('shopeepay', 'merchantId', e.target.value)}
                placeholder="Shopee Merchant ID"
                helperText="Your Shopee merchant identifier"
              />
              
              <TextField
                fullWidth
                label="API Key"
                type="password"
                value={walletConfig.apiKey}
                onChange={(e) => handleConfigChange('shopeepay', 'apiKey', e.target.value)}
                placeholder="Shopee API Key"
                helperText="API key from Shopee developer portal"
              />
              
              <TextField
                fullWidth
                label="Secret Key"
                type="password"
                value={walletConfig.secretKey}
                onChange={(e) => handleConfigChange('shopeepay', 'secretKey', e.target.value)}
                placeholder="Shopee Secret Key"
                helperText="Secret key for request signing"
              />
            </Box>
          );

        case 'zalopay':
          return (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>ZaloPay Integration Process:</strong>
                  <br />1. Register business with VNG (ZaloPay parent company)
                  <br />2. Submit Vietnamese business license
                  <br />3. Complete KYC verification
                  <br />4. Get approved for merchant services
                </Typography>
              </Alert>

              <TextField
                fullWidth
                label="App ID"
                value={walletConfig.appId}
                onChange={(e) => handleConfigChange('zalopay', 'appId', e.target.value)}
                placeholder="ZaloPay App ID"
                helperText="Application ID assigned by ZaloPay"
              />
              
              <TextField
                fullWidth
                label="Key 1 (MAC Key)"
                type="password"
                value={walletConfig.key1}
                onChange={(e) => handleConfigChange('zalopay', 'key1', e.target.value)}
                placeholder="MAC Key for request signing"
                helperText="Primary key for transaction MAC generation"
              />
              
              <TextField
                fullWidth
                label="Key 2 (Callback Key)"
                type="password"
                value={walletConfig.key2}
                onChange={(e) => handleConfigChange('zalopay', 'key2', e.target.value)}
                placeholder="Callback verification key"
                helperText="Key for verifying callback authenticity"
              />
              
              <TextField
                fullWidth
                label="Merchant Code"
                value={walletConfig.merchantCode}
                onChange={(e) => handleConfigChange('zalopay', 'merchantCode', e.target.value)}
                placeholder="VNG assigned merchant code"
                helperText="Unique merchant identifier from VNG"
              />

              <TextField
                fullWidth
                label="Vietnamese Business License"
                value={walletConfig.businessLicense}
                onChange={(e) => handleConfigChange('zalopay', 'businessLicense', e.target.value)}
                placeholder="Business license number"
                helperText="Vietnamese GPKD (Business License) number"
              />

              <TextField
                fullWidth
                label="Tax Code"
                value={walletConfig.taxCode}
                onChange={(e) => handleConfigChange('zalopay', 'taxCode', e.target.value)}
                placeholder="Vietnamese tax code"
                helperText="MST (Tax Identification Number) in Vietnam"
              />

              <FormControl fullWidth>
                <InputLabel>Integration Type</InputLabel>
                <Select
                  value={walletConfig.integrationType || 'standard'}
                  onChange={(e) => handleConfigChange('zalopay', 'integrationType', e.target.value)}
                  label="Integration Type"
                >
                  <MenuItem value="standard">Standard Integration</MenuItem>
                  <MenuItem value="gateway">Gateway Integration</MenuItem>
                  <MenuItem value="mini-app">Zalo Mini App</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Callback URL"
                value={walletConfig.callbackUrl}
                onChange={(e) => handleConfigChange('zalopay', 'callbackUrl', e.target.value)}
                placeholder="https://yourdomain.com/zalopay/callback"
                helperText="URL for ZaloPay payment notifications"
              />
            </Box>
          );

        case 'paylah':
          return (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>PayLah! Integration Process:</strong>
                  <br />1. Apply for DBS Merchant Account
                  <br />2. Register for PayLah! QR Merchant Service
                  <br />3. Get QR Code from DBS Business Portal
                  <br />4. Configure static/dynamic QR generation
                </Typography>
              </Alert>

              <TextField
                fullWidth
                label="DBS Merchant Account Number"
                value={walletConfig.merchantAccount}
                onChange={(e) => handleConfigChange('paylah', 'merchantAccount', e.target.value)}
                placeholder="Your DBS business account number"
                helperText="Primary DBS business account for PayLah! settlements"
              />
              
              <TextField
                fullWidth
                label="PayLah! Merchant ID"
                value={walletConfig.merchantId}
                onChange={(e) => handleConfigChange('paylah', 'merchantId', e.target.value)}
                placeholder="PayLah! merchant identifier"
                helperText="Merchant ID from DBS PayLah! Business Portal"
              />
              
              <TextField
                fullWidth
                label="QR Code ID"
                value={walletConfig.qrCodeId}
                onChange={(e) => handleConfigChange('paylah', 'qrCodeId', e.target.value)}
                placeholder="Static QR code identifier"
                helperText="QR code ID for this terminal (from DBS portal)"
              />
              
              <TextField
                fullWidth
                label="Business UEN"
                value={walletConfig.businessUEN}
                onChange={(e) => handleConfigChange('paylah', 'businessUEN', e.target.value)}
                placeholder="Singapore business UEN"
                helperText="Unique Entity Number for business verification"
              />
              
              <TextField
                fullWidth
                label="Business Name"
                value={walletConfig.businessName}
                onChange={(e) => handleConfigChange('paylah', 'businessName', e.target.value)}
                placeholder="Business name for receipts"
                helperText="Name displayed to customers in PayLah! app"
              />

              <FormControl fullWidth>
                <InputLabel>QR Code Type</InputLabel>
                <Select
                  value={walletConfig.qrType || 'static'}
                  onChange={(e) => handleConfigChange('paylah', 'qrType', e.target.value)}
                  label="QR Code Type"
                >
                  <MenuItem value="static">Static QR (Fixed amount)</MenuItem>
                  <MenuItem value="dynamic">Dynamic QR (Variable amount)</MenuItem>
                </Select>
              </FormControl>

              {walletConfig.qrType === 'dynamic' && (
                <>
                  <TextField
                    fullWidth
                    label="API Endpoint"
                    value={walletConfig.apiEndpoint}
                    onChange={(e) => handleConfigChange('paylah', 'apiEndpoint', e.target.value)}
                    placeholder="https://api.dbs.com/v1/paylah"
                    helperText="DBS PayLah! API endpoint for dynamic QR"
                  />
                  
                  <TextField
                    fullWidth
                    label="API Key"
                    type="password"
                    value={walletConfig.apiKey}
                    onChange={(e) => handleConfigChange('paylah', 'apiKey', e.target.value)}
                    placeholder="DBS API Key"
                    helperText="API key from DBS Developer Portal"
                  />
                </>
              )}
            </Box>
          );

        case 'bigpay':
          return (
            <Box sx={{ display: 'grid', gap: 2 }}>
              <TextField
                fullWidth
                label="Merchant ID"
                value={walletConfig.merchantId}
                onChange={(e) => handleConfigChange('bigpay', 'merchantId', e.target.value)}
                placeholder="BigPay Merchant ID"
                helperText="Merchant ID provided by BigPay"
              />
              
              <TextField
                fullWidth
                label="API Key"
                type="password"
                value={walletConfig.apiKey}
                onChange={(e) => handleConfigChange('bigpay', 'apiKey', e.target.value)}
                placeholder="BigPay API Key"
                helperText="API key from BigPay merchant portal"
              />
              
              <TextField
                fullWidth
                label="Secret Key"
                type="password"
                value={walletConfig.secretKey}
                onChange={(e) => handleConfigChange('bigpay', 'secretKey', e.target.value)}
                placeholder="BigPay Secret Key"
                helperText="Secret key for API authentication"
              />
            </Box>
          );
        
      default:
        return (
          <Alert severity="info">
            Configuration form for {walletProviders[wallet]?.name} will be available soon.
          </Alert>
        );
    }
  };

  const handleSave = () => {
    onSave({
      ...config,
      setupCompleted: config[config.walletType].connected,
      lastUpdated: new Date().toISOString()
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AccountBalanceWallet />
          <Typography variant="h6">Digital Wallet Setup</Typography>
          {paymentMethod && (
            <Chip label={paymentMethod.name} color="primary" size="small" />
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
          <Tab label="Configuration" />
          <Tab label="Integration" />
          <Tab label="Testing" />
        </Tabs>

        {/* Tab 1: Configuration */}
        {activeTab === 0 && (
          <Box>
            {/* Wallet Info Card */}
            <Card sx={{ mb: 3, bgcolor: 'primary.lighter' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {walletProviders[config.walletType]?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {walletProviders[config.walletType]?.description}
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  {walletProviders[config.walletType]?.regions.map((region) => (
                    <Chip key={region} label={region} size="small" />
                  ))}
                </Box>
                
                <Link 
                  href={walletProviders[config.walletType]?.documentation} 
                  target="_blank"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <Api fontSize="small" />
                  Developer Documentation
                </Link>
              </CardContent>
            </Card>

            <FormControlLabel
              control={
                <Switch
                  checked={config.testMode}
                  onChange={(e) => setConfig(prev => ({ ...prev, testMode: e.target.checked }))}
                />
              }
              label="Test Mode (Use sandbox environment)"
              sx={{ mb: 3 }}
            />

            {renderWalletConfig()}

            <Button
              variant="contained"
              onClick={() => handleTestConnection(config.walletType)}
              disabled={!config[config.walletType].merchantId}
              startIcon={config[config.walletType].connected ? <CheckCircle /> : <Business />}
              color={config[config.walletType].connected ? 'success' : 'primary'}
              sx={{ mt: 2 }}
            >
              {config[config.walletType].connected ? 'Connected' : 'Test Connection'}
            </Button>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Requirements:</strong>
                <br />
                {walletProviders[config.walletType]?.requirements.map((req, index) => (
                  <span key={index}>• {req}<br /></span>
                ))}
              </Typography>
            </Alert>
          </Box>
        )}

        {/* Tab 2: Integration Settings */}
        {activeTab === 1 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Integration Settings</Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Environment</InputLabel>
                  <Select
                    value={config.integration.environment}
                    onChange={(e) => handleConfigChange('integration', 'environment', e.target.value)}
                    label="Environment"
                  >
                    <MenuItem value="sandbox">Sandbox (Testing)</MenuItem>
                    <MenuItem value="production">Production (Live)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Webhook URL"
                  value={config.integration.webhookUrl}
                  onChange={(e) => handleConfigChange('integration', 'webhookUrl', e.target.value)}
                  placeholder="https://yourdomain.com/webhooks/wallet"
                  helperText="URL to receive payment notifications"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Return URL"
                  value={config.integration.callbackUrl}
                  onChange={(e) => handleConfigChange('integration', 'callbackUrl', e.target.value)}
                  placeholder="https://yourdomain.com/payment/return"
                  helperText="URL customers return to after payment"
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Tab 3: Testing */}
        {activeTab === 2 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Test Your Integration</Typography>
            
            <Alert severity="warning" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Testing Guidelines:</strong>
                <br />• Always test in sandbox environment first
                <br />• Use test accounts provided by the wallet provider
                <br />• Verify webhook notifications are received
                <br />• Test various payment scenarios (success, failure, timeout)
              </Typography>
            </Alert>

            <Button
              variant="outlined"
              fullWidth
              disabled={!config[config.walletType].connected}
              sx={{ mt: 2 }}
            >
              Run Integration Test
            </Button>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          disabled={!config[config.walletType].connected}
        >
          Save Configuration
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DigitalWalletSetupDialog;